import { useAtom, useSetAtom } from "jotai";
import { useReactFlow } from "@xyflow/react";
import { nodesAtom, edgesAtom, zslAtom } from "@zapier/canvas-state";
import { validateZSL } from "./validateZSL";
import { ComposerProvider } from "@zapier/agent-system";
import { parseCanvasDataFromZSL } from "@zapier/streaming/src/utils/zslToCanvas";
import { ComposerInitializer } from "../ComposerInitializer";
import { zodSchema } from "@ai-sdk/ui-utils";
/**
 * TEMPORARY TYPE DECLARATION
 * This is a temporary solution to fix type errors with @zapier/zsl-creator-schemas
 * This should be replaced with proper type declarations from the package in the future
 */
// @ts-ignore
import { ModifiedFinalZSLSchema } from "@zapier/zsl-creator-schemas";
function ComposerInitializerComponent({
  children,
}: {
  children: React.ReactNode;
}) {
  ComposerInitializer();
  return children;
}

export function CanvasComposerHydrator({
  children,
}: {
  children: React.ReactNode;
}) {
  const { fitView } = useReactFlow();
  const setNodes = useSetAtom(nodesAtom);
  const setEdges = useSetAtom(edgesAtom);
  const [zsl, setZsl] = useAtom(zslAtom);

  const handleZslToCanvas = async (zsl: any) => {
    try {
      setZsl(zsl);
      const nodesAndEdges = await parseCanvasDataFromZSL(zsl);
      if (nodesAndEdges) {
        const {
          canvas: { nodes, edges },
        } = nodesAndEdges;
        setNodes(nodes);
        setEdges(edges as any);
        fitView();
      }
    } catch (error) {
      console.error("Error converting ZSL to Canvas:", error);
    }
  };

  const canvasTools = {
    update_ui: {
      description: "Necessary to update the UI with the created zsl.",
      parameters: zodSchema(ModifiedFinalZSLSchema).jsonSchema,
      execute: async (args: any) => {
        await handleZslToCanvas(args);
        const zsl = args as any;
        const validationResult = await validateZSL({
          parameters: [],
          ...zsl,
        });

        if (!validationResult.valid) {
          const warningMessage =
            validationResult.errorMessage || "Validation failed";
          return {
            content: [
              {
                type: "text",
                text: `Canvas updated but validation failed.\n${warningMessage}`,
              },
            ],
          };
        } else {
          return {
            content: [{ type: "text", text: "Canvas updated" }],
          };
        }
      },
    },
  };

  return (
    <ComposerProvider
      orchestratorHost="canvas"
      tools={canvasTools}
      initialAdditionalData={{
        zsl,
      }}
    >
      <ComposerInitializerComponent>{children}</ComposerInitializerComponent>
    </ComposerProvider>
  );
}
